import {LicenseApplicationForm} from "@/models/LicenseApplicationForm";
import {Property} from "@/models/Property";
import {License} from "@/models/License";
import {Listing} from "@/models/Listing";
import {postApi} from "@/api";
import { licenseRepository } from "@/data";
import { licenseDatesEqual } from '@strenforcement/domain/licenseDates';
import { getLicenseAppliedOn } from '@/data/licenseDates';
import { todayString } from '@strenforcement/domain/licenseExpiration';
import {
  LicenseContact,
  getCustomFieldValue as getCustomFieldValueDomain,
  setCustomFieldValue as setCustomFieldValueDomain,
  computeCustomFieldData,
  buildLicenseEditorIndexKeys,
  buildLicenseEditorPayload
} from "@strenforcement/domain/licenseEditor";

export class LicenseEditorModel {

  constructor(city, data, id) {
    this.city = city;
    this.id = id;
    this.form = new LicenseApplicationForm(city);
    this.loading = false;
    this.submitting = false;
    this.propertyLoadSequence = 0;

    // Owner info
    this.contact = new LicenseContact();

    // Listings
    this.listings = [];
    this.preExistingListings = [];

    this.parse(data);

    this.isCustomComplete = true;
  }

  get isNew() {
    return !this.id;
  }

  get isComplete() {
    return this.isAddressComplete;// && this.contact.isComplete;
  }

  get parcelId() {
    return this._property && this._property.id;
  }

  get cityId() {
    return this.city && this.city.id;
  }

  get propertyId() {
    return this._propertyId;
  }

  set propertyId(propertyId) {
    this._propertyId = propertyId;
    this.listings = [];
    this.findMatchingProperty();

    if (!this.city.isPublicView) {
      this.findMatchingListings();
    }
  }

  async findMatchingProperty() {
    const sequence = ++this.propertyLoadSequence;
    this.loading = true;
    try {
      const property = this._propertyId ? (await this.city.getProperty(this._propertyId)) : null;
      // A slower lookup for a previously selected parcel must not replace its owner's defaults.
      if (sequence === this.propertyLoadSequence) this.property = property;
    } finally {
      if (sequence === this.propertyLoadSequence) this.loading = false;
    }
  }

  async findMatchingListings() {
    this.loading = true;
    try {
      let {results} = await Listing.query(this.city, {constraints:[['verifiedPropertyId', '==', this.propertyId]]});
      this.preExistingListings = results;
    } catch (e) {
      this.loading = false;
    }
  }

  get property() {
    return this._property;
  }

  set property(property) {
    this._property = property;
    this.contact.defaultOwners = property?.owners || [];
    this.contact.defaultAddress = property?.details?.ownerAddress || property?.data?.ownerAddress || '';
  }

  get addressFormatted() {
    return this.property?.addressFormatted || '';
  }

  get hasCustomFields() {
    return Boolean(this.form?.published && this.form.supportsCustomFields);
  }

  get customFields() {
    return this.form.fields;
  }

  getCustomFieldValue(id) {
    return getCustomFieldValueDomain(this.customFieldValues, id);
  }

  setCustomFieldValue(id, value) {
    this.customFieldValues = setCustomFieldValueDomain(this.customFieldValues, id, value);
  }

  get customFieldData() {
    return computeCustomFieldData(this.customFieldValues);
  }

  get isAddressComplete() {
    return !!this.propertyId;
  }

  indexKeys() {
    return buildLicenseEditorIndexKeys({
      addressFormatted: this.addressFormatted,
      id: this.id,
      contactName: (this.contact || {}).name || ''
    });
  }

  parse(data) {
    data = {...data};
    this._propertyId = data.propertyId;
    this._property = data.property ? Property.dataConverter(this.city)(data.property) : null;
    this.contact = new LicenseContact(data.contact)

    // Listings
    this.listings = data.listings || [];

    // Custom Fields
    this.customFieldValues = data.customFieldValues || [];

    // Admin fields
    this.appliedOn = getLicenseAppliedOn(data);
    if (this.isNew && this.appliedOn == null && data.appliedOn === undefined) this.appliedOn = todayString();
    this.status = data.status || this.city.defaultLicenseStatus;
    this.paid = data.paid || false;
    this.licenseId = data.licenseId || null;
    this.issuedOn = data.issuedOn ?? data.dateIssued ?? null;
    this.originalDates = { appliedOn: this.appliedOn, issuedOn: this.issuedOn };
  }

  serialize(includeProperty) {
    const data = buildLicenseEditorPayload({
      propertyId: this._propertyId,
      contact: this.contact,
      preExistingListingUrls: this.preExistingListings.map(listing => listing.url),
      listings: this.listings,
      customFieldValues: this.customFieldValues,
      appliedOn: this.appliedOn,
      status: this.status,
      paid: this.paid,
      licenseId: this.licenseId,
      issuedOn: this.issuedOn,
      includeProperty,
      property: this._property
    });
    if (!this.isNew) {
      delete data.licenseId;
      if (licenseDatesEqual(this.appliedOn, this.originalDates.appliedOn, { dateOnly: true })) delete data.appliedOn;
      if (licenseDatesEqual(this.issuedOn, this.originalDates.issuedOn, { dateOnly: true })) delete data.issuedOn;
    }
    return data;
  }

  async submit() {
    this.submitting = true;

    try {
      const response = await postApi(`/v1/public/cities/${encodeURIComponent(this.cityId)}/licenses`, {
        data: {
          propertyId: this.propertyId,
          contact: this.contact.serialize(),
          listings: this.listings,
          customFieldValues: this.customFieldValues
        }
      });

      return response.receipt;
    } finally {
      this.submitting = false;
    }
  }

  async saveEdits({ approvalNotificationDecision, emailNotificationDecisions } = {}) {
    this.submitting = true;
    try {
      const data = this.serialize(false);
      let response;
      if (this.isNew) {
        response = await licenseRepository.createLicense(this.cityId, {
          data
        });
      } else {
        response = await licenseRepository.updateLicense(this.cityId, this.id, {
          data,
          ...(approvalNotificationDecision ? { approvalNotificationDecision } : {}),
          ...(emailNotificationDecisions ? { emailNotificationDecisions } : {})
        });
      }
      const license = License.oneFromApi(this.city, response.license);
      license.approvalNotification = response.approvalNotification || null;
      license.emailNotifications = response.emailNotifications || [];
      this.originalDates = { appliedOn: this.appliedOn, issuedOn: this.issuedOn };
      return license;
    } finally {
      this.submitting = false;
    }
  }

  async updateLicenseId(licenseId) {
    if (this.isNew) {
      throw new Error('Cannot update the ID of a license that has not been created');
    }
    const response = await licenseRepository.updateLicenseId(this.cityId, this.id, {
      licenseId
    });
    const license = License.oneFromApi(this.city, response.license);
    this.licenseId = license.data.licenseId;
    return license;
  }

  get collection() {
    return this.city.subCollection('licenses');
  }

}
